// src/services/user/auth.ts

import { UserControllerApi } from "@/http/apis/UserControllerApi";
import { CompanyInfoControllerApi } from "@/http/apis/CompanyInfoControllerApi";
import { UserWalletControllerApi } from "@/http/apis/UserWalletControllerApi";
import { VerifyCodeControllerApi } from "@/http/apis/VerifyCodeControllerApi";
import { OAuthOAuthControllerApi } from "@/http/apis/OAuthOAuthControllerApi";
import type { CompanyInfoVo } from "@/http/models/CompanyInfoVo";
import type { UserPasswordReqVo } from "@/http/models/UserPasswordReqVo";

const userApi = new UserControllerApi();
const companyApi = new CompanyInfoControllerApi();
const walletApi = new UserWalletControllerApi();
const verifyCodeApi = new VerifyCodeControllerApi();
const oauthApi = new OAuthOAuthControllerApi();

export const loginService = {
  /** 获取 Kakao 官方授权地址 */
  async getKakaoAuthorizeUrl() {
    const res = await oauthApi.authOauthKakaoAuthorizeGet();
    if (!res.data?.authorizeUrl) {
      throw new Error(res.message || "获取 Kakao 授权地址失败");
    }
    return res.data.authorizeUrl;
  },

  /** 用回调中的一次性登录码换取系统登录信息 */
  async loginByOAuthCode(loginCode: string) {
    const res = await oauthApi.authOauthLoginCodePost({
      oAuthCodeExchangeReqVo: { loginCode },
    });
    if (!res.data) throw new Error(res.message || "Kakao 登录失败");
    return res.data;
  },

  /** 用回调中的一次性交接码换取短期绑定凭证 */
  async exchangeOAuthBindCode(code: string) {
    const res = await oauthApi.authOauthBindCodePost({
      oAuthBindCodeExchangeReqVo: { code },
    });
    if (!res.data?.oauthBindToken) {
      throw new Error(res.message || "获取 Kakao 绑定凭证失败");
    }
    return res.data.oauthBindToken;
  },

  /** 向已有账号手机号发送 Kakao 绑定验证码 */
  async sendOAuthBindMobileCode(oauthBindToken: string, mobileNumber: string) {
    return await oauthApi.authOauthBindMobileCodePost({
      oAuthBindMobileCodeReqVo: { oauthBindToken, mobileNumber },
    });
  },

  /** 向已有会员邮箱发送 OAuth 绑定验证码 */
  async sendOAuthBindEmailCode(oauthBindToken: string, email: string) {
    return await oauthApi.authOauthBindEmailCodePost({
      oAuthBindEmailCodeReqVo: { oauthBindToken, email },
    });
  },

  /** 将 Kakao 绑定到已有账号并直接登录 */
  async bindOAuthAccount(
    oauthBindToken: string,
    mobileNumber: string,
    verificationCode: string,
  ) {
    const res = await oauthApi.authOauthBindPost({
      oAuthBindReqVo: { oauthBindToken, mobileNumber, verificationCode },
    });
    if (!res.data) throw new Error(res.message || "Kakao 账号绑定失败");
    return res.data;
  },

  /** 使用邮箱验证码绑定已有账号并登录 */
  async bindOAuthAccountByEmail(
    oauthBindToken: string,
    email: string,
    verificationCode: string,
  ) {
    const res = await oauthApi.authOauthBindEmailPost({
      oAuthBindEmailReqVo: { oauthBindToken, email, verificationCode },
    });
    if (!res.data) throw new Error(res.message || "Kakao 账号绑定失败");
    return res.data;
  },

  /**
   * 账号密码登录
   */
  async loginByAccount(account: string, password: string) {
    const res = await userApi.userLoginPost({
      userLoginReqVo: {
        account,
        password,
      },
    });
    return res.data;
  },

  /**
   * 退出登录
   */
  async logout(userId: number) {
    return await userApi.userIdLogoutPut({
      id: userId,
    });
  },
  /**
   * 获取用户信息
   */
  async getUserInfo(userId: number) {
    const res = await userApi.userIdGet({
      id: userId,
    });
    return res.data;
  },
  /**
   * 获取用户统计信息
   */
  async getUserStatisticsInfo(userId: number) {
    const res = await userApi.userGetUserStatisticsInfoUserIdGet({
      userId,
    });
    return res.data;
  },
  /**
   * 修改用户信息
   */
  async updateUserInfo(userId: number, payload: { name?: string; nameEn?: string }) {
    const res = await userApi.userIdUpdatePost({
      id: userId,
      updateUserReqVo: payload,
    });
    return res;
  },
  /** 发送修改密码短信验证码 */
  async sendPasswordVerifyCode(userId: number) {
    return await userApi.userSendPasswordVerifyCodeGet({ userId });
  },
  /** 使用短信验证码修改登录密码 */
  async changePassword(payload: UserPasswordReqVo) {
    return await userApi.userPasswordPut({
      userPasswordReqVo: payload,
    });
  },
  /**
   * 创建韩国企业信息
   */
  async createKoreanCompany(payload: CompanyInfoVo) {
    const res = await companyApi.companyInfoCreatePost({
      companyInfoVo: payload,
    });
    return res;
  },
  /**
   * 注销用户
   */
  async disableUser(userId: number) {
    const res = await userApi.userIdDisablePut({
      id: userId,
    });
    return res;
  },
  /** 发送短信登录验证码 */
  async sendLoginMobileVerificationCode(mobileNumber: string) {
    const res = await userApi.userSendLoginMobileVerificationCodeGet({
      mobileNumber,
    });
    if (res.code !== 0) throw new Error(res.message || "发送验证码失败");
    return res;
  },

  /** 确认短信登录验证码 */
  async confirmLoginMobileVerificationCode(
    mobileNumber: string,
    verificationCode: string,
  ) {
    return await verifyCodeApi.verifyCodeConfirmPost({
      verifyCodeConfirmReqVo: {
        type: 1,
        target: mobileNumber,
        code: verificationCode,
        scene: "LOGIN",
      },
    });
  },

  /** 手机号验证码登录 */
  async loginByMobile(mobileNumber: string, verificationCode: string) {
    const res = await userApi.userLoginByMobilePost({
      userMobileLoginReqVo: {
        mobileNumber,
        verificationCode,
      },
    });
    if (res.code !== 0 || !res.data) {
      throw new Error(res.message || "手机号登录失败");
    }
    return res.data;
  },

  /**
   * 获取当前用户钱包信息
   */
  async getCurrentWallet() {
    const res = await walletApi.userWalletCurrentGet({});
    return res?.data;
  },
};
